import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Card } from "@booking-seconds/ui";
import { formatCurrency } from "@booking-seconds/utils";
import { apiFetch } from "./lib/api";
import { destroyStripeElementSafely } from "./lib/stripe";

type StripeInstance = ReturnType<NonNullable<typeof window.Stripe>>;
type StripeElements = ReturnType<StripeInstance["elements"]>;
type StripePaymentElement = ReturnType<StripeElements["create"]>;

export type StripePaymentSession = {
  publishableKey: string;
  clientSecret: string;
  paymentIntentId: string;
  setupIntentId?: string;
  status: string;
  amount: number;
  mode?: "payment" | "authorization" | "setup" | "not_required";
};

export type StripePaymentCardHandle = {
  confirmPayment: () => Promise<string>;
};

function loadStripeScript() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (window.Stripe) {
    return Promise.resolve();
  }

  if (!window.__bookingSecondsStripeLoader) {
    window.__bookingSecondsStripeLoader = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>('script[data-stripe-script="true"]');
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener("error", () => reject(new Error("Unable to load Stripe.")), { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.async = true;
      script.defer = true;
      script.dataset.stripeScript = "true";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Unable to load Stripe."));
      document.head.appendChild(script);
    });
  }

  return window.__bookingSecondsStripeLoader;
}

export const StripePaymentCard = forwardRef<
  StripePaymentCardHandle,
  {
    token?: string;
    session?: StripePaymentSession | null;
    onPaymentSuccess?: (status: string) => void;
    hideConfirmButton?: boolean;
    wrapInCard?: boolean;
    dueLabel?: string;
    eyebrow?: string;
    title?: string;
    subtitle?: string;
    disclosure?: string;
    emptyMessage?: string;
  }
>(function StripePaymentCard(
  {
    token,
    session,
    onPaymentSuccess,
    hideConfirmButton = false,
    wrapInCard = true,
    dueLabel,
    eyebrow,
    title: titleOverride,
    subtitle,
    disclosure,
    emptyMessage = "No payment is due right now."
  },
  ref
) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const stripeRef = useRef<StripeInstance | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const paymentElementRef = useRef<StripePaymentElement | null>(null);
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);

  const paymentSessionQuery = useQuery({
    queryKey: ["public-payment-session", token],
    queryFn: () =>
      apiFetch<StripePaymentSession>(`/api/bookings/public/${token}/payment-session`, {
        method: "POST"
      }),
    enabled: Boolean(token) && !session,
    refetchOnWindowFocus: false
  });

  const paymentSession = session ?? paymentSessionQuery.data ?? null;
  const sessionMode = paymentSession?.mode ?? "payment";
  const defaultEyebrow = sessionMode === "authorization" || sessionMode === "setup" ? "Authorization" : "Payment";
  const defaultTitle =
    sessionMode === "authorization"
      ? "Pre-authorization"
      : sessionMode === "setup"
        ? "Save payment method"
        : paymentSession
          ? dueLabel || `Pay ${formatCurrency(paymentSession.amount)}`
          : "Stripe checkout";
  const defaultSubtitle =
    sessionMode === "authorization" || sessionMode === "setup"
      ? "Securely save your payment method."
      : "Use Stripe to complete this payment securely.";
  const defaultDisclosure =
    sessionMode === "setup"
      ? "We will authorize the card closer to the service date, then capture the approved amount after service."
      : sessionMode === "authorization"
        ? "Your card will not be charged today. We’ll only pre-authorize and capture payment after your service is completed."
        : "By paying with a card, you authorize us to securely save this payment method with Stripe for this booking and future approved balances.";
  const bodyDisclosure = disclosure ?? defaultDisclosure;
  const title = titleOverride || defaultTitle;
  const renderBody = (content: ReactNode) =>
    wrapInCard ? (
      <Card eyebrow={eyebrow || defaultEyebrow} title={title}>
        {content}
      </Card>
    ) : (
      <div className="stack">
        <p className="eyebrow">{eyebrow || defaultEyebrow}</p>
        <h3>{title}</h3>
        {content}
      </div>
    );

  useEffect(() => {
    if (!paymentSession?.clientSecret || !mountRef.current) {
      return;
    }

    let isActive = true;

    loadStripeScript()
      .then(() => {
        if (!isActive || !mountRef.current || !window.Stripe || !paymentSession.clientSecret) {
          return;
        }

        mountRef.current.innerHTML = "";
        stripeRef.current = window.Stripe(paymentSession.publishableKey);
        elementsRef.current = stripeRef.current.elements({
          clientSecret: paymentSession.clientSecret,
          appearance: {
            theme: "stripe"
          }
        });
        paymentElementRef.current = elementsRef.current.create("payment");
        paymentElementRef.current.mount(mountRef.current);
      })
      .catch((error) => {
        if (!isActive) {
          return;
        }
        setPaymentMessage(error instanceof Error ? error.message : "Unable to load Stripe payment form.");
      });

    return () => {
      isActive = false;
      const paymentElement = paymentElementRef.current;
      paymentElementRef.current = null;
      elementsRef.current = null;
      stripeRef.current = null;
      destroyStripeElementSafely(paymentElement);
    };
  }, [paymentSession?.clientSecret, paymentSession?.publishableKey]);

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!stripeRef.current || !elementsRef.current) {
        throw new Error("Stripe payment form is not ready yet.");
      }

      const result =
        paymentSession?.mode === "setup"
          ? await stripeRef.current.confirmSetup({
              elements: elementsRef.current,
              redirect: "if_required"
            })
          : await stripeRef.current.confirmPayment({
              elements: elementsRef.current,
              redirect: "if_required"
            });

      if (result.error?.message) {
        throw new Error(result.error.message);
      }

      if ("paymentIntent" in result) {
        return result.paymentIntent?.status ?? "submitted";
      }
      if ("setupIntent" in result) {
        return result.setupIntent?.status ?? "submitted";
      }
      return "submitted";
    },
    onSuccess: async (status) => {
      setPaymentMessage(
        status === "requires_capture"
          ? "Authorization approved. We will capture the approved amount after service."
          : status === "succeeded" && paymentSession?.mode === "setup"
            ? "Payment method saved."
          : status === "succeeded"
          ? "Payment succeeded. This page will refresh in a moment."
          : "Payment submitted. Stripe is processing the payment."
      );
      if (!session) {
        await paymentSessionQuery.refetch();
      }
      onPaymentSuccess?.(status);
    }
  });

  useImperativeHandle(ref, () => ({
    confirmPayment: () => confirmMutation.mutateAsync()
  }));

  if (!session && paymentSessionQuery.isLoading) {
    return renderBody(<p className="card-copy">Preparing your secure payment form...</p>);
  }

  if (!session && paymentSessionQuery.error) {
    return renderBody(<p className="error-text">{(paymentSessionQuery.error as Error).message}</p>);
  }

  if (!paymentSession || paymentSession.status === "not_required" || paymentSession.amount <= 0) {
    return renderBody(<p className="card-copy">{emptyMessage}</p>);
  }

  return renderBody(
    <>
      <div className="stack">
        <p className="card-copy">{subtitle ?? defaultSubtitle}</p>
        {bodyDisclosure ? <p className="card-copy">{bodyDisclosure}</p> : null}
        <div className="stripe-payment-element" ref={mountRef} />
        {paymentMessage ? <p className="card-copy">{paymentMessage}</p> : null}
        {confirmMutation.error ? <p className="error-text">{(confirmMutation.error as Error).message}</p> : null}
        {!hideConfirmButton ? (
          <Button onClick={() => confirmMutation.mutate()} disabled={confirmMutation.isPending}>
            {confirmMutation.isPending
              ? "Processing..."
              : dueLabel ||
                (sessionMode === "authorization"
                  ? `Authorize ${formatCurrency(paymentSession.amount)}`
                  : sessionMode === "setup"
                    ? "Save payment method"
                    : `Pay ${formatCurrency(paymentSession.amount)}`)}
          </Button>
        ) : null}
      </div>
    </>
  );
});
